import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { IUserAuthContract } from "../../student/auth/model";
import { createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { CompletedTopicRequestRecord, ITopic } from "../../shared/entities/topics";
import { $currentCourseId, currentCourseIdChanged, ICurrentCourseContract } from "../../courses/ui/current";
import { IEnrollmentsContract } from "@features/enrollments/model";
import { toastShow } from "@features/notification/model";
import { ICustomError } from "@features/shared/entities/notification";
import { CourseDataApi } from "@features/shared/api/course-data.api";
import { TopicCompleted } from "@features/shared/events";
import { currentTopicIdChanged } from "@features/topics/ui/current";
import { IPlatformContextContract } from "@features/launch-context/model";

export const topicsLoaded = createEvent();
export const completeTopic = createEvent<CompletedTopicRequestRecord>();

export const $topics = createStore<ITopic[]>([]);
export const $completedTopicId = createStore<number | null>(null);

sample({
    clock: completeTopic,
    fn: (data) => data.topicId,
    target: $completedTopicId
});

export const getTopicsByCourseIdFx = createEffect(async (courseId: number) => {
    const response = await CourseDataApi.getTopicsByCourseId(courseId);

    return response;
});

export const topicCompletedFx = createEffect(async (data: CompletedTopicRequestRecord) => {
    const response = await CourseDataApi.completeTopic(data);
    return response;
});

const trackTopicCompletedFx = createEffect(
    ({ topic_id, topic_name, course_id }: { topic_id: number; topic_name: string; course_id: number }) => {
        TopicCompleted(course_id, topic_id, topic_name);
    }
);

sample({
    source: {
        topics: $topics,
        completedTopicId: $completedTopicId,
        courseId: $currentCourseId
    },
    clock: topicCompletedFx.done,
    filter: ({ completedTopicId, courseId }) => completedTopicId !== null && courseId !== null,
    fn: ({ topics, completedTopicId, courseId }) => {
        const topic = topics.find((t) => t.id === completedTopicId);
        return {
            topic_id: topic?.id ?? -1,
            topic_name: topic?.title ?? "unknown",
            course_id: courseId as number
        };
    },
    target: trackTopicCompletedFx
});

sample({
    clock: completeTopic,
    target: topicCompletedFx
});

sample({
    clock: [getTopicsByCourseIdFx.fail, topicCompletedFx.fail],
    filter: ({ error }: { error: ICustomError }) => error?.error?.title !== null,
    fn: ({ error }: { error: ICustomError }) => error?.error,
    target: toastShow
});

const contract = new ModelContract(
    {
        $topics
    },
    {
        topicsLoaded,
        completeTopic
    },
    {},
    {
        getTopicsByCourseIdFx,
        topicCompletedFx
    }
);

export type ITopicsContract = typeof contract;

export class TopicsModule extends ModuleDescription<ITopicsContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public currentCourseModule: ICurrentCourseContract,
        public enrollmentsModule: IEnrollmentsContract,
        public launchContextModule: IPlatformContextContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            source: { platform: this.launchContextModule.stores.$platform, user: this.userAuthModule.stores.$student },

            clock: getTopicsByCourseIdFx.doneData,
            fn: (s, p) => {
                if (s.platform === "web") {
                    return p;
                }
                const userHasSubscription = s.user?.status === "active" || s.user?.status === "redemption";
                if (userHasSubscription) {
                    return p;
                }
                return p.filter((it) => it.availableInTrial);
            },
            target: [$topics, topicsLoaded]
        });

        sample({
            clock: this.currentCourseModule.events.currentCourseIdChanged,
            filter: (courseId) => courseId !== null,
            fn: (courseId) => courseId,
            target: getTopicsByCourseIdFx
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: $topics
        });

    }
}
